import React, { useState, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import ProductItem from './ProductItem.component';
import UI from '../../ui';
import Data from '../../data';

const { WrappedButton, WrappedButtonType } = UI;
const { BarcodeTypes, CalculatedData } = Data;

const ProductDetails = ({
	products,
	showMobileView,
	isMobile,
	editingInbound,
	saveInbound,
	updateInstructions,
	validations,
	updateValidation,
}) => {
	const [productsList, setProductsList] = useState([]);

	useEffect(() => {
		const updatedValidations = validations;

		// Anything in here is fired on component mount.
		if (isEmpty(editingInbound.items)) {
			const defaultProductList = [
				{
					productId: 'product1',
					name: '',
					sku: '',
					quantityExpected: null,
					barcode: '',
					barcodeType: BarcodeTypes.UPC,
					isBarcodeEditable: true,
				},
			];
			setProductsList(defaultProductList);

			updatedValidations.product = {
				0: {
					product: 0,
					quantity: 0,
					barcode: 0,
				},
			};
			updateValidation(updatedValidations);
			saveInbound({ items: defaultProductList }, false);
		} else {
			setProductsList([...editingInbound.items]);
		}
	}, [editingInbound]);

	// Build Product Options
	const productOptions = products.reduce((options, productItem) => {
		options.push({
			productLabel: `${productItem.sku} (${productItem.name})`,
			productId: productItem.productId,
			barcode: productItem.productBarcode,
			barcodeType: productItem.barcodeType,
		});
		return options;
	}, []);

	const onProductItemChange = (index, name, value) => {
		const newProductList = [...productsList];
		const productItem = newProductList[index];

		productItem[name] = value;

		if (name === 'productId') {
			const product = products.find(record => record.productId === value);
			productItem.name = product.name;
			productItem.sku = product.sku;

			if (product.productBarcode) {
				productItem.isBarcodeEditable = false;
				productItem.barcode = product.productBarcode;
				productItem.barcodeType = product.barcodeType
					? product.barcodeType
					: CalculatedData.getBarcodeType(product.productBarcode);
			} else {
				productItem.isBarcodeEditable = true;
				productItem.barcode = '';
				productItem.barcodeType = BarcodeTypes.UPC;
			}
		}

		saveInbound({ items: newProductList });
	};

	const addProduct = () => {
		const newProductList = [...productsList];
		const updatedValidations = validations;
		const productId = `product${newProductList.length + 1}`;

		updatedValidations.product[newProductList.length] = {
			product: 0,
			quantity: 0,
			barcode: 0,
		};

		newProductList.push({
			productId,
			name: '',
			sku: '',
			quantityExpected: null,
			barcode: '',
			barcodeType: BarcodeTypes.UPC,
			isBarcodeEditable: true,
		});

		saveInbound({ items: newProductList });
		updateValidation(updatedValidations);
	};

	const onDeleteButtonClick = index => {
		const newProductList = [...productsList];
		const updatedValidations = validations;

		// Shifts validations for products
		for (let x = index; x < newProductList.length - 1; x++) {
			updatedValidations.product[x] = updatedValidations.product[x + 1];
		}

		// Remove product from productsList
		newProductList.splice(index, 1);
		saveInbound({ items: newProductList });

		// Remove product validation
		delete updatedValidations.product[newProductList.length];
		updateValidation(updatedValidations);
	};

	const updateProductInstructions = (header, instructions) => {
		updateInstructions('product', header, instructions);
	};

	const mobileClassName = showMobileView ? 'mobile-wrapper' : '';

	return (
		<div className={`common-inbound-details-product-details ${mobileClassName}`}>
			<div className="product-details-header">Product Details</div>

			<div className="product-details-content">
				<div className="p-grid p-col-12 p-dir-col product-list">
					{!showMobileView && (
						<div className="sub-header p-grid p-col-12 p-dir-row">
							<p className="p-lg-5">Product</p>
							<p className="p-lg-2">Quantity</p>
							<p className="p-lg-3">Barcode</p>
							<p className="p-lg-2">Barcode Type</p>
						</div>
					)}

					{productsList.length > 0 &&
						productsList.map((productItem, index) => (
							<ProductItem
								key={`${productItem.productId}_index${index}`}
								productOptions={productOptions}
								showMobileView={showMobileView}
								isMobile={isMobile}
								onActionTrigger={onProductItemChange}
								productIndex={index}
								productItem={productItem}
								onDeleteButtonClick={onDeleteButtonClick}
								updateProductInstructions={updateProductInstructions}
								validations={validations}
								updateValidation={updateValidation}
							/>
						))}
				</div>

				<WrappedButton
					buttonType={WrappedButtonType.Icon}
					label="Add another product"
					icon="add-icon"
					className="add-product"
					onClick={addProduct}
					dataqa="add-another-product"
				/>
			</div>
		</div>
	);
};

export default ProductDetails;
